<template>
  <!-- Comparativo de latência (administradores): a mesma simulação em várias configurações, com as
       saídas de cada execução conferidas contra as da primeira -->
  <v-dialog :value="bench.open" persistent max-width="1100" scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card>
      <v-card-title class="text-h6">
        Latency comparison
        <span class="text-caption ml-2 grey--text">{{ bench.kind.toUpperCase() }} · {{ bench.title || "Untitled" }}</span>
        <v-spacer />
        <v-btn icon aria-label="Close" :disabled="bench.running" @click="bench.open = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <template v-if="bench.running">
          <p v-if="bench.comparing" class="mb-2">Comparing the outputs of every run with the first one…</p>
          <p v-else class="mb-2">
            Running {{ bench.current + 1 }} of {{ total }}:
            <strong>{{ configs[bench.current] && configs[bench.current].label }}</strong>
            <span v-if="hasReps && configs[bench.current]"> (repetition {{ configs[bench.current].rep }})</span>
          </p>
          <v-progress-linear
            :value="(100 * bench.results.length) / total"
            :indeterminate="bench.comparing"
            color="primary"
            class="mb-4"
          />
        </template>
        <v-alert v-if="bench.error" type="error" dense text>{{ bench.error }}</v-alert>

        <template v-if="bench.results.length">
          <ResponsivePlot v-if="summary.length" :data="chart" :layout="layout" />
          <v-simple-table dense class="mt-2">
            <thead>
              <tr>
                <th>Configuration</th>
                <th v-if="hasReps" class="text-right">Rep.</th>
                <th>Machines used</th>
                <th class="text-right">Processes</th>
                <th class="text-right">Queue</th>
                <th class="text-right">Solver</th>
                <th class="text-right">Job</th>
                <th class="text-right">Click to result</th>
                <th class="text-right">vs. first</th>
                <th class="text-right" title="Largest difference of the outputs against the first run">Max. difference vs. first</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in bench.results" :key="index">
                <td>
                  {{ row.label }}
                  <div class="text-caption grey--text">{{ row.requested }}</div>
                </td>
                <td v-if="hasReps" class="text-right num">{{ row.rep }}</td>
                <td>
                  <span v-if="row.status === 'failed'" class="error--text" :title="row.error">
                    <v-icon small color="error">mdi-alert-circle</v-icon> failed
                  </span>
                  <span v-else>{{ row.nodes || "—" }}</span>
                  <div v-if="row.status === 'failed'" class="text-caption error--text">{{ row.error }}</div>
                </td>
                <td class="text-right num">{{ processes(row) }}</td>
                <td class="text-right num">{{ fmt(row.queueS) }}</td>
                <td class="text-right num">{{ fmt(row.solverS) }}</td>
                <td class="text-right num">{{ fmt(row.jobS) }}</td>
                <td class="text-right num"><strong>{{ fmt(row.wallS) }}</strong></td>
                <td class="text-right num">{{ speedup(row) }}</td>
                <td class="text-right num">
                  <span :title="diffTitle()">{{ diffText(row) }}</span>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
          <p class="text-caption mt-2 mb-0">
            <strong>Click to result</strong>: from sending the simulation to receiving the answer (what the user waits).
            <strong>Job</strong>: time on the machines (container start, solver and output files).
            <strong>Queue</strong>: wait for free machines. <strong>vs. first</strong> &gt; 1× means faster than the
            first run. <strong>Max. difference</strong>: largest difference of the outputs against the first run —
            {{ bench.kind === "3d"
              ? "field |E| on the cut, relative to its maximum"
              : "field map relative to its maximum, and spectra (T, R, loss) in absolute value" }};
            0 means identical. Each run is also listed in the admin panel (/admin/cluster) with its outputs.
          </p>
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="bench.running || !bench.results.length" @click="downloadCsv">
          <v-icon left small>mdi-download</v-icon> Download CSV
        </v-btn>
        <v-btn color="primary" text :disabled="bench.running" @click="bench.open = false">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { adminRun } from "@/common/adminRun";
import { benchmarkCsv } from "@/common/benchmark";
import ResponsivePlot from "@/components/ResponsivePlot";

const mean = values => values.reduce((a, b) => a + b, 0) / values.length;
const std = values => {
  if (values.length < 2) return 0;
  const m = mean(values);
  return Math.sqrt(values.reduce((a, b) => a + (b - m) ** 2, 0) / (values.length - 1));
};

export default {
  name: "BenchmarkDialog",
  components: { ResponsivePlot },
  computed: {
    bench() {
      return adminRun.benchmark;
    },
    configs() {
      return adminRun.configs || [];
    },
    total() {
      return Math.max(this.configs.length, this.bench.results.length, 1);
    },
    hasReps() {
      return this.bench.results.some(r => r.rep > 1) || this.configs.some(c => c.rep > 1);
    },
    baseline() {
      const first = this.bench.results.find(r => r.status === "completed");
      return first ? first.wallS : null;
    },
    // Média e desvio padrão do tempo do clique ao resultado por configuração (só execuções concluídas)
    summary() {
      const groups = [];
      this.bench.results.forEach(r => {
        if (r.status !== "completed") return;
        let group = groups.find(g => g.label === r.label);
        if (!group) groups.push((group = { label: r.label, values: [] }));
        group.values.push(r.wallS);
      });
      return groups.map(g => ({ label: g.label, mean: mean(g.values), std: std(g.values), n: g.values.length }));
    },
    chart() {
      const rows = this.summary;
      return [{
        type: "bar",
        orientation: "h",
        y: rows.map(r => r.label),
        x: rows.map(r => r.mean),
        error_x: this.hasReps ? { type: "data", array: rows.map(r => r.std), visible: true, thickness: 1.5, width: 4 } : undefined,
        marker: { color: this.$vuetify.theme.dark ? "#3987e5" : "#2a78d6" },
        customdata: rows.map(r => `n = ${r.n}${r.n > 1 ? ` · σ ${r.std.toFixed(2)} s` : ""}`),
        hovertemplate: "%{y}: %{x:.2f} s<br>%{customdata}<extra></extra>"
      }];
    },
    layout() {
      const dark = this.$vuetify.theme.dark;
      const axis = { color: "#898781", gridcolor: dark ? "#2c2c2a" : "#e1e0d9", zeroline: false };
      return {
        height: 60 + 36 * this.summary.length,
        margin: { l: 110, r: 16, t: 8, b: 36 },
        paper_bgcolor: "rgba(0,0,0,0)",
        plot_bgcolor: "rgba(0,0,0,0)",
        font: { family: "system-ui, -apple-system, 'Segoe UI', sans-serif", color: dark ? "#c3c2b7" : "#52514e", size: 12 },
        showlegend: false,
        bargap: 0.3,
        xaxis: { ...axis, title: { text: this.hasReps ? "Click to result, mean ± σ (s)" : "Click to result (s)",
          font: { size: 11 } }, rangemode: "tozero" },
        yaxis: { ...axis, type: "category", autorange: "reversed", automargin: true }
      };
    }
  },
  methods: {
    fmt(value) {
      return Number.isFinite(value) ? `${value.toFixed(2)} s` : "—";
    },
    processes(row) {
      if (!row.ranks) return "—";
      return row.threads > 1 ? `${row.ranks} × ${row.threads}` : `${row.ranks}`;
    },
    speedup(row) {
      if (row.status !== "completed" || !this.baseline || !row.wallS) return "—";
      return `${(this.baseline / row.wallS).toFixed(2)}×`;
    },
    sci(value) {
      if (!Number.isFinite(value)) return "—";
      return value === 0 ? "0 (identical)" : value.toExponential(1);
    },
    diffText(row) {
      if (row.status !== "completed" || row.fieldDiff == null) return "—";
      if (this.bench.kind === "3d" || row.spectraDiff == null) return this.sci(row.fieldDiff);
      return `${this.sci(row.fieldDiff)} · ${this.sci(row.spectraDiff)}`;
    },
    diffTitle() {
      if (this.bench.kind === "3d") return "field |E| on the cut (relative)";
      return "field map (relative) · spectra (absolute)";
    },
    downloadCsv() {
      const blob = new Blob([benchmarkCsv(this.bench)], { type: "text/csv;charset=utf-8" });
      const link = document.createElement("a");
      const stamp = (this.bench.startedAt || new Date().toISOString()).slice(0, 19).replace(/[:T]/g, "-");
      const name = (this.bench.title || "simulation").replace(/[^A-Za-z0-9_-]+/g, "-").slice(0, 40);
      link.href = URL.createObjectURL(blob);
      link.download = `electrosfi-latency-${this.bench.kind}-${name}-${stamp}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    }
  }
};
</script>

<style scoped>
.num {
  font-variant-numeric: tabular-nums;
}
</style>
